import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReturnOrderLine } from 'returnCenter-api';
import styled from 'styled-components';

import Button from 'components/ui/Button';
import Flex from 'components/ui/Flex';
import RightDrawer from 'components/ui/Drawer/RightDrawer';
import Text from 'components/ui/Text';
import { HandleReturnItemChange } from 'components/Orders/Order';
import { ExtendedReturnItem } from 'store/reducers/shared/sliceTypes';

const EXTERNAL_COMMENT_MAX_LENGTH = 50;

export type CommentsDrawerTarget = {
    orderNumber: string;
    orderline: ReturnOrderLine;
    returnItem: ExtendedReturnItem;
};

type CommentsDrawerProps = {
    open: boolean;
    onClose: () => void;
    target: CommentsDrawerTarget | null;
    disabled?: boolean;
    handleReturnItemChange: HandleReturnItemChange;
};

const CommentsDrawer = ({ open, onClose, target, disabled = false, handleReturnItemChange }: CommentsDrawerProps) => {
    const { t } = useTranslation();
    const [internal, setInternal] = useState('');
    const [external, setExternal] = useState('');

    // Sync local buffer with target whenever drawer opens for a (new) row.
    useEffect(() => {
        if (open && target) {
            setInternal(target.returnItem.comment ?? '');
            setExternal(target.returnItem.externalComment ?? '');
        }
    }, [open, target]);

    const handleSave = () => {
        if (!target) return;
        const { orderNumber, orderline, returnItem } = target;
        const sku = orderline.sku ?? '';
        const orderLineNumber = orderline.orderLineNumber ?? '';
        const itemId = returnItem.id ?? returnItem.splitId ?? -1;
        const originalInternal = returnItem.comment ?? '';
        const originalExternal = returnItem.externalComment ?? '';

        if (internal !== originalInternal) {
            handleReturnItemChange(orderNumber, sku, orderLineNumber, itemId, 'comment', internal);
        }
        if (external !== originalExternal) {
            handleReturnItemChange(orderNumber, sku, orderLineNumber, itemId, 'externalComment', external);
        }
        onClose();
    };

    const productName = target?.orderline.productName ?? '';
    const sku = target?.orderline.sku ?? '';

    return (
        <RightDrawer
            open={open}
            onClose={onClose}
            title={t('notes', 'Notes')}
            footer={
                <Flex justify="end" gap="0.5rem">
                    <Button rounded onClick={onClose} type="button">
                        {t('cancel')}
                    </Button>
                    <Button rounded main onClick={handleSave} type="button" disabled={disabled}>
                        {t('save')}
                    </Button>
                </Flex>
            }
        >
            <Flex column gap="1rem">
                {target && (
                    <ProductHeader column gap="0.125rem">
                        {productName && (
                            <Text fontSize={14} bold margin="0">
                                {productName}
                            </Text>
                        )}
                        {sku && (
                            <Text fontSize={12} color="textLight" margin="0">
                                {sku}
                            </Text>
                        )}
                    </ProductHeader>
                )}

                {target?.returnItem.customerComment && (
                    <Field column gap="0.375rem">
                        <Text fontSize={13} bold margin="0">
                            {t('customerComment', 'Customer comment')}
                        </Text>
                        <StyledTextarea
                            rows={3}
                            value={target.returnItem.customerComment}
                            disabled
                            readOnly
                        />
                    </Field>
                )}

                <Field column gap="0.375rem">
                    <Text fontSize={13} bold margin="0">
                        {t('notes')}
                    </Text>
                    <StyledTextarea
                        rows={4}
                        value={internal}
                        disabled={disabled}
                        onChange={(e) => setInternal(e.target.value)}
                    />
                </Field>

                <hr />
                <Field column gap="0.375rem">
                    <Flex align="center" justify="between" gap="0.5rem">
                        <Text fontSize={13} bold margin="0">
                            {t('externalComment')}
                        </Text>
                        <Text fontSize={12} color="textLight" margin="0">
                            {external.length}/{EXTERNAL_COMMENT_MAX_LENGTH}
                        </Text>
                    </Flex>
                    <StyledTextarea
                        rows={3}
                        value={external}
                        maxLength={EXTERNAL_COMMENT_MAX_LENGTH}
                        disabled={disabled}
                        onChange={(e) => setExternal(e.target.value)}
                    />
                </Field>
            </Flex>
        </RightDrawer>
    );
};

export default CommentsDrawer;

const ProductHeader = styled(Flex)`
    padding-bottom: 0.75rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const Field = styled(Flex)``;

const StyledTextarea = styled.textarea`
    width: 100%;
    padding: 0.5rem 0.75rem;
    font: inherit;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.text};
    background-color: ${({ theme, disabled }) => (disabled ? theme.colors.disabled : theme.colors.fillWhite)};
    border: 1px solid ${({ theme }) => theme.colors.borderLight};
    border-radius: var(--radius);
    resize: vertical;
    transition: border-color 0.15s ease;

    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.colors.main};
    }

    &:disabled {
        cursor: not-allowed;
    }
`;

